import type { DataSource, Entity, WebcamEntity, MapBounds } from '../types/entity';

/**
 * Windy Webcams API v3. Key usada direto do client é o modelo oficial
 * da Windy pra free tier (não é um segredo de billing como as demais 4
 * keys sensíveis do projeto) — por isso é uma das duas, junto da WSDOT,
 * que dispensa Cloud Function (exceção documentada em app/.env.example).
 *
 * Docs v3: https://api.windy.com/webcams/api/v3/docs
 * Schema confirmado contra: https://api.windy.com/webcams/version-transfer
 * (guia oficial de migração v2→v3, que lista o schema completo lado a
 * lado — usado aqui porque o Swagger interativo em /docs carrega o spec
 * via JS client-side e não expõe o JSON estático pro web_fetch).
 *
 * Free tier: imagem em baixa resolução, token de imagem expira em 10min,
 * paginação limitada a offset 1.000.
 *
 * CONFIRMADO contra a doc de migração (não é mais suposição):
 * - Resposta de lista: `{ total: number; webcams: WebcamV3Schema[] }`
 *   (sem envelope `status: "OK"` — isso era V2)
 * - `webcamId: number` (não string)
 * - `lastUpdatedOn` é string ISO 8601 direto no objeto raiz do webcam
 * - `images.current` (não `image.current` — isso era V2)
 * - `player.live` em V3 é uma STRING de link direto pro embed, não mais
 *   um objeto `{available, embed}` (isso era V2) — corrigido abaixo
 * - Existe filtro nativo `bbox` em V3 (`?bbox=north,east,south,west`),
 *   estritamente melhor que `nearby` com raio fixo pra "webcams dentro
 *   da área visível do mapa" — trocado abaixo
 *
 * AINDA NÃO CONFIRMADO CONTRA V3 ESPECIFICAMENTE (suposição herdada de
 * um exemplo de resposta V2 encontrado publicamente, onde o container
 * se chamava `image` no singular): as sub-chaves de tamanho dentro de
 * `images.current` são `icon` | `thumbnail` | `preview` | `toenail`. A
 * doc de migração não lista essas sub-chaves explicitamente pra V3 (só
 * diz "all other URLs for available sizes"). Mantido `preview` como
 * aposta mais razoável, mas se a function/client logar `previewUrl:
 * null` de forma consistente, essa é a primeira coisa a inspecionar via
 * `console.log(JSON.stringify(data.webcams[0], null, 2))` numa chamada
 * real.
 */
const WINDY_API_BASE = 'https://api.windy.com/webcams/api/v3';

interface WindyWebcamImages {
  current?: Record<string, string>; // chaves possíveis: icon, thumbnail, preview, toenail (ver nota acima)
}

interface WindyWebcamPlayer {
  live?: string; // V3: string de link direto, não {available, embed} (isso era V2)
  day?: string;
}

interface WindyWebcamItem {
  webcamId: number;
  title: string;
  location: {
    latitude: number;
    longitude: number;
  };
  lastUpdatedOn?: string;
  images?: WindyWebcamImages;
  player?: WindyWebcamPlayer;
}

interface WindyWebcamsListResponse {
  total: number;
  webcams: WindyWebcamItem[];
}

function toWebcamEntity(item: WindyWebcamItem): WebcamEntity {
  return {
    id: `webcam-windy-${item.webcamId}`,
    layer: 'webcams-windy',
    lat: item.location.latitude,
    lon: item.location.longitude,
    label: item.title,
    severity: 'normal',
    observedAt: item.lastUpdatedOn ?? new Date().toISOString(),
    raw: {
      title: item.title,
      previewUrl: item.images?.current?.preview ?? null,
      playerUrl: item.player?.live ?? null,
      source: 'windy',
    },
  };
}

export function createWindyWebcamsSource(apiKey: string): DataSource {
  return {
    layer: 'webcams-windy',
    label: 'Câmeras (Windy)',
    requiresClientKey: true,
    viaCloudFunction: false,

    async fetchEntities(bounds?: MapBounds): Promise<Entity[]> {
      const url = new URL(`${WINDY_API_BASE}/webcams`);

      // bbox nativo do V3 é a escolha certa pra "o que está visível no
      // mapa agora" — muito melhor que nearby com raio fixo, que ou
      // corta área visível (raio pequeno) ou traz lixo fora de tela
      // (raio grande). Formato confirmado: north,east,south,west.
      if (bounds) {
        url.searchParams.set(
          'bbox',
          `${bounds.north.toFixed(4)},${bounds.east.toFixed(4)},${bounds.south.toFixed(4)},${bounds.west.toFixed(4)}`,
        );
      }
      url.searchParams.set('limit', '50');
      url.searchParams.set('include', 'images,location,player');

      const res = await fetch(url.toString(), {
        headers: { 'x-windy-api-key': apiKey },
      });

      if (!res.ok) {
        throw new Error(`Windy Webcams respondeu ${res.status}`);
      }

      const data = (await res.json()) as WindyWebcamsListResponse;
      return data.webcams.map(toWebcamEntity);
    },
  };
}
